import { useLocation, useNavigate } from "@solidjs/router";
import type { Scope } from "@storytell/cp-client";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { batch, createEffect, createMemo, onCleanup, onMount } from "solid-js";
import { createStore } from "solid-js/store";
import { isServer } from "solid-js/web";
import type { AssetsService } from "~/stores/assets";

export const createChatServiceScope = (deps: {
  projectId: () => string | undefined;
  assets: AssetsService;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Scope management
  const [state, setState] = createStore({
    allKnowledge: false,
    concepts: [] as string[],
    labels: [] as string[],
    assets: [] as string[],
    views: [] as string[],
    /**
     * Special holder for assets that are being uploaded.
     * They do not have an ID yet, but we need to show them in the scope.
     */
    uploadingAssets: [] as { id: string; filename: string; status: "uploading" | "error" }[],
    /**
     * Flag to track if user explicitly removed "All Knowledge" to allow blank scope
     */
    userExplicitlyRemovedAllKnowledge: false,
  });

  const navigateToNewChat = () => {
    if (location.pathname !== "/" && !location.pathname.startsWith("/chat")) {
      // The all knowledge modal resets the url to remove its state from the search params
      // We need to wait for that to be done before navigating the user to the home page
      requestAnimationFrame(() => {
        navigate("/");
      });
    }
  };

  // Helper to check if the current project has assets
  const projectHasAssets = createMemo(() => {
    const projectId = deps.projectId();
    if (!projectId) return false;

    const allAssets = deps.assets.all();
    return allAssets.some((asset) => asset.projectId === projectId);
  });

  // Check if there's any defined scope (assets, concepts, labels, views, or allKnowledge)
  const hasDefinedScope = createMemo(() => {
    return (
      state.allKnowledge ||
      state.assets.length > 0 ||
      state.concepts.length > 0 ||
      state.labels.length > 0 ||
      state.views.length > 0
    );
  });

  // Set default to "All Knowledge" if project has assets and no scope is defined
  const setDefaultScopeIfNeeded = () => {
    if (projectHasAssets() && !hasDefinedScope() && !state.userExplicitlyRemovedAllKnowledge) {
      setState("allKnowledge", true);
    }
  };

  // Watch for changes in project assets and scope to set default when needed (client-side only)
  createEffect(() => {
    if (isServer) return;
    projectHasAssets();
    setDefaultScopeIfNeeded();
  });

  // Watch for route changes to ensure default scope is set when navigating to "/"
  createEffect(() => {
    if (isServer) return;
    const pathname = location.pathname;
    if (pathname === "/") {
      setDefaultScopeIfNeeded();
    }
  });

  // --- Event Subscriptions ---
  onMount(() => {
    const unsubscribeAdded = subscribeToGlobalEvent("added-mention-to-prompt-bar", (event) => {
      batch(() => {
        // User is defining a scope, so clear "All Knowledge"
        if (state.allKnowledge) {
          setAllKnowledge(false);
          setState("userExplicitlyRemovedAllKnowledge", false);
        }

        if (event.detail.id.startsWith("asset_")) {
          addAsset(event.detail.id);
        } else if (event.detail.id.startsWith("concept_")) {
          addConcept(event.detail.id);
        } else if (event.detail.id.startsWith("label_")) {
          addLabel(event.detail.id);
        } else if (event.detail.id.startsWith("view_")) {
          addView(event.detail.id);
        }
      });

      navigateToNewChat();
    });
    const unsubscribeRemoved = subscribeToGlobalEvent("removed-mention-from-prompt-bar", (event) => {
      batch(() => {
        if (event.detail.id.startsWith("asset_")) {
          removeAsset(event.detail.id);
        } else if (event.detail.id.startsWith("concept_")) {
          removeConcept(event.detail.id);
        } else if (event.detail.id.startsWith("label_")) {
          removeLabel(event.detail.id);
        } else if (event.detail.id.startsWith("view_")) {
          removeView(event.detail.id);
        }
      });

      // Check if we should restore default scope after removing items
      requestAnimationFrame(() => {
        if (!hasDefinedScope() && !state.userExplicitlyRemovedAllKnowledge) {
          setDefaultScopeIfNeeded();
        }
      });

      navigateToNewChat();
    });

    const unsubscribeAddedToPromptScope = subscribeToGlobalEvent("add-items-to-prompt-scope", (event) => {
      batch(() => {
        if (event.detail.allKnowledge) {
          setAllKnowledge(true);
          setState("userExplicitlyRemovedAllKnowledge", false);
        } else {
          // User defined a specific scope, so clear "All Knowledge"
          setAllKnowledge(false);
          // Reset the flag since user is defining a scope
          setState("userExplicitlyRemovedAllKnowledge", false);
        }

        for (const id of event.detail.ids) {
          if (id.startsWith("asset_")) {
            addAsset(id);
          } else if (id.startsWith("concept_")) {
            addConcept(id);
          } else if (id.startsWith("label_")) {
            addLabel(id);
          } else if (id.startsWith("view_")) {
            addView(id);
          }
        }
      });

      navigateToNewChat();
    });

    const unsubscribeUserContextChanged = subscribeToGlobalEvent("user-context-changed", () => {
      clearAll();
    });

    onCleanup(() => {
      unsubscribeAdded();
      unsubscribeRemoved();
      unsubscribeAddedToPromptScope();
      unsubscribeUserContextChanged();
    });
  });

  const addView = (viewId: string) => {
    if (state.views.includes(viewId)) return;
    batch(() => {
      // When specific views are added, clear "All Knowledge" to show individual items
      if (state.allKnowledge) {
        setAllKnowledge(false);
      }
      setState("views", (prev) => [...prev, viewId]);
    });
  };
  const removeView = (viewId: string) => {
    setState("views", (prev) => prev.filter((id) => id !== viewId));
  };

  const addLabel = (labelId: string) => {
    if (state.labels.includes(labelId)) return;
    batch(() => {
      // When specific labels are added, clear "All Knowledge" to show individual items
      if (state.allKnowledge) {
        setAllKnowledge(false);
      }
      setState("labels", (prev) => [...prev, labelId]);
    });
  };
  const removeLabel = (labelId: string) => {
    setState("labels", (prev) => prev.filter((id) => id !== labelId));
  };
  const addAsset = (assetId: string) => {
    if (state.assets.includes(assetId)) return;
    batch(() => {
      // When specific assets are added, clear "All Knowledge" to show individual assets
      if (state.allKnowledge) {
        setAllKnowledge(false);
      }
      setState("assets", (prev) => [...prev, assetId]);
    });
  };
  const addUploadingAsset = (filename: string) => {
    // Random enough id to avoid collisions
    const assetId = `temp_${performance.now()}_${Math.random().toString(36).substring(2, 9)}`;
    if (state.uploadingAssets.some((a) => a.id === assetId)) return;
    setState("uploadingAssets", (prev) => [...prev, { id: assetId, filename, status: "uploading" }]);
    addAsset(assetId);
    return assetId;
  };
  const updateUploadingAssetStatus = (assetId: string, status: "uploading" | "error") => {
    setState("uploadingAssets", (prev) => prev.map((a) => (a.id === assetId ? { ...a, status } : a)));
  };
  const removeUploadingAsset = (assetId: string) => {
    setState("uploadingAssets", (prev) => prev.filter((a) => a.id !== assetId));
    removeAsset(assetId);
  };
  const removeAsset = (assetId: string) => {
    setState("assets", (prev) => prev.filter((id) => id !== assetId));
    dispatchGlobalEvent("removed-item-from-prompt-scope", { id: assetId });
  };
  const addConcept = (conceptId: string) => {
    if (state.concepts.includes(conceptId)) return;
    batch(() => {
      // When specific concepts are added, clear "All Knowledge" to show individual items
      if (state.allKnowledge) {
        setAllKnowledge(false);
      }
      setState("concepts", (prev) => [...prev, conceptId]);
    });
  };
  const removeConcept = (conceptId: string) => {
    setState("concepts", (prev) => prev.filter((id) => id !== conceptId));
    dispatchGlobalEvent("removed-item-from-prompt-scope", { id: conceptId });
  };

  const clearAll = () => {
    const toRemove = [...state.assets, ...state.concepts];
    batch(() => {
      setState({
        assets: [],
        concepts: [],
        labels: [],
        views: [],
      });
      // Reset the explicit removal flag when clearing
      setState("userExplicitlyRemovedAllKnowledge", false);
      // Set allKnowledge to false first
      setState("allKnowledge", false);
    });

    // Set default after removing items - check immediately and also defer for safety
    setDefaultScopeIfNeeded();
    requestAnimationFrame(() => {
      setDefaultScopeIfNeeded();
    });

    for (const id of toRemove) {
      dispatchGlobalEvent("removed-item-from-prompt-scope", {
        id,
      });
    }
  };

  const assets = () => state.assets;
  const concepts = () => state.concepts;
  const views = () => state.views;
  const labels = () => state.labels;
  const allKnowledge = () => state.allKnowledge;
  const uploadingAssets = () => state.uploadingAssets;

  const setAllKnowledge = (allKnowledge: boolean) => {
    batch(() => {
      setState("allKnowledge", allKnowledge);
      if (allKnowledge) {
        // If setting to true, reset the flag
        setState("userExplicitlyRemovedAllKnowledge", false);
      } else {
        // If setting to false, check if there are any other scopes defined
        // If not, this means user explicitly removed "All Knowledge" with no other scope
        const hasOtherScope =
          state.assets.length > 0 || state.concepts.length > 0 || state.labels.length > 0 || state.views.length > 0;

        if (!hasOtherScope) {
          // User explicitly removed "All Knowledge" with no other scope
          setState("userExplicitlyRemovedAllKnowledge", true);
        }
      }
    });
  };

  // TODO: @andi Add views to scope
  const getScopeForPromptSubmit = (): Scope.Scope => {
    const base: Scope.Scope = {
      $typeName: "query.v3.Scope",
      viewIdList: [],
      textAttachmentList: [],
      attachmentIdList: [],
      assetIdList: [],
      artifactIdList: [],
      labelIdList: [],
      conceptIdList: [],
      projectIdList: [],
      workspaceId: "",
      organizationId: "",
    };
    if (state.allKnowledge && deps.projectId()) {
      return {
        ...base,
        assetIdList: [],
        conceptIdList: [],
        labelIdList: [],
        projectIdList: [deps.projectId()!],
      };
    }

    return {
      ...base,
      assetIdList: state.assets,
      conceptIdList: state.concepts,
      labelIdList: state.labels,
      viewIdList: state.views,
      projectIdList: [],
    };
  };

  const loadScopeFromMessage = (scope: Scope.Scope) => {
    batch(() => {
      // Check if this was an "all knowledge" scope
      if (scope.projectIdList && scope.projectIdList.length > 0) {
        setAllKnowledge(true);
      } else {
        setAllKnowledge(false);

        // Load assets
        setState("assets", scope.assetIdList || []);

        // Load concepts
        setState("concepts", scope.conceptIdList || []);

        // Load labels
        setState("labels", scope.labelIdList || []);

        // Load views
        setState("views", scope.viewIdList || []);
      }

      // Dispatch events to notify other components about the scope changes
      for (const assetId of scope.assetIdList || []) {
        dispatchGlobalEvent("added-mention-to-prompt-bar", { id: assetId });
      }
      for (const conceptId of scope.conceptIdList || []) {
        dispatchGlobalEvent("added-mention-to-prompt-bar", { id: conceptId });
      }
      for (const labelId of scope.labelIdList || []) {
        dispatchGlobalEvent("added-mention-to-prompt-bar", { id: labelId });
      }
      for (const viewId of scope.viewIdList || []) {
        dispatchGlobalEvent("added-mention-to-prompt-bar", { id: viewId });
      }
    });
  };

  return {
    assets,
    addLabel,
    removeLabel,
    views,
    addView,
    removeView,
    addUploadingAsset,
    concepts,
    labels,
    allKnowledge,
    setAllKnowledge,
    addAsset,
    removeAsset,
    clearAll,
    addConcept,
    removeConcept,
    getScopeForPromptSubmit,
    loadScopeFromMessage,
    uploadingAssets,
    removeUploadingAsset,
    updateUploadingAssetStatus,
  };
};
